// HUDA PORT — component: product card.
// Ported from the static site's js/product-card.js (source of truth).
// - Injects the hover image for cards carrying data-hover-src, so cards
//   without one (the bundle card) stay in lift-only mode with no markup change.
// - Wires the card's add-to-cart form.
//
// WHAT CHANGED FROM THE SOURCE, and only this: the source's `wireCart` was a
// STUB — it swallowed the click and swapped the label to «أضيف ✓» for 1200ms
// without touching any cart. Here that same visible behaviour is driven by a
// real POST to Shopify's Ajax cart. The constants, the label, the 1200ms revert
// and the aria-live announcement are carried verbatim; only the plumbing under
// them is real. This is the brief's "the cart plumbing becomes Shopify".
//
// Exposed as window.HDProductCard.init(root) so cards built after parse can opt
// in, exactly as the source did. init() also runs itself over the document at
// parse time, so server-rendered cards need no call of their own.
//
// PROGRESSIVE ENHANCEMENT: every cart control is a real <form> that POSTs to
// /cart/add and lands on /cart with no JS at all. This file only intercepts it.
// If the fetch fails for any reason the native submit is allowed through, so a
// shopper never ends up with a dead button.
//
// PHASE 3 added the cart drawer on top, and changed three things here and only
// these: the request now asks for the drawer section back, a successful add
// hands that HTML to window.HDCart and opens the drawer, and the promise chain
// is SPLIT so no drawer failure can reach the native-resubmit catch. The label
// swap to «أضيف ✓» and its 1200ms revert are untouched and are now the
// secondary confirmation rather than the only one. Every drawer call is
// null-guarded on window.HDCart: this file is loaded per-section from the body
// while hd-cart.js is loaded from the layout head, and neither may assume the
// other arrived.
(function () {
  'use strict';

  var CART_DONE = 'أضيف ✓'; // fallback; the real string comes from the locale
  var CART_REVERT = 1200;
  // the section FILENAME, which is also its Section Rendering API id
  var CART_SECTION = 'hd-cart-drawer';
  // one attribute guards both behaviours: a card is either wired or it isn't,
  // so init() can be called again over an overlapping root without doubling
  // the hover layer or stacking a second click listener
  var MARK = 'data-pc-bound';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function addHover(card) {
    var media = card.querySelector('.product-card__media');
    if (!media) return;
    var img = document.createElement('img');
    img.className = 'product-card__img product-card__img--hover';
    img.src = card.getAttribute('data-hover-src');
    img.alt = '';
    img.loading = 'lazy';
    img.setAttribute('aria-hidden', 'true');
    media.appendChild(img);
  }

  // PHASE 3 — the first-add gap this comment used to describe is CLOSED.
  // sections/hd-header.liquid now renders the count node on every page and
  // carries emptiness on a `hidden` attribute, so there is always something to
  // write into. Reading /cart.js rather than deriving the number client-side
  // keeps it correct when Shopify partially fulfils an add (it returns 422 but
  // still adds what it could).
  //
  // This is now the FALLBACK path only. When the drawer is present its refresh
  // already carried the authoritative count on the swapped node, so the add
  // handler skips this call entirely and saves the round-trip.
  function refreshCartCount() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        // HDCart.setCount also toggles `hidden` and updates the visually-hidden
        // sentence that gives the link its accessible name; the bare DOM write
        // below is what remains if hd-cart.js never loaded.
        if (window.HDCart) {
          window.HDCart.setCount(cart.item_count);
          return;
        }
        var el = document.querySelector('.hd-nav__cart-count');
        if (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        }
      })
      .catch(function () {
        /* the count is cosmetic; never let it break the add */
      });
  }

  // Behavioural twin of the source's stub, with a real cart underneath.
  function wireCartForm(form) {
    var btn = form.querySelector('[type="submit"]');
    if (!btn) return;

    var label = btn.textContent;
    var done = form.getAttribute('data-done') || CART_DONE;
    var timer = null;

    // the confirmation is a text swap on the focused button; aria-live gets
    // it announced by screen readers
    btn.setAttribute('aria-live', 'polite');

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var fd = new FormData(form);

      // Appended to the FormData in JS ONLY, never as a hidden <input> in the
      // Liquid: a hidden field would join the no-JS payload AND the
      // form.submit() fallback payload, where it means nothing. Guarding on
      // window.HDCart means a page where hd-cart.js failed to load asks for no
      // sections at all and gets exactly the response shape that shipped before
      // this branch. sections_url MUST begin with '/' — pathname always does,
      // and an invalid value 400s the WHOLE request even though the add
      // succeeded.
      if (window.HDCart) {
        fd.append('sections', CART_SECTION);
        fd.append('sections_url', window.location.pathname);
      }

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (json) {
          // the Ajax cart signals failure with a `status` key on the body
          if (json && json.status) throw new Error(json.description || json.message);
          btn.textContent = done;
          if (timer) clearTimeout(timer);
          timer = setTimeout(function () {
            btn.textContent = label;
            timer = null;
          }, CART_REVERT);
          return json;
        })
        // ── THE ADD-FAILURE DOMAIN ENDS HERE ──────────────────────────────
        // This catch is a NATIVE RE-POST of the add form, not a no-op, so
        // nothing that runs after a SUCCESSFUL add may be allowed to reach it.
        // Before the split below, a drawer error — a mistyped section name, a
        // section that came back null inside an HTTP 200, a DOMParser miss,
        // window.HDCart not yet defined — would land here and add the item a
        // SECOND time, then navigate away to /cart showing quantity 2. Silent,
        // intermittent, and only reproducible when the drawer is already broken.
        .catch(function () {
          // hard fallback: let the browser do the real POST, which lands the
          // shopper on /cart. Never leave the click with nothing to show.
          form.submit();
          return null;
        })
        .then(function (json) {
          if (!json) return; // the native submit is already in flight
          var swapped = false;
          try {
            // never assumed present: a section that fails to render comes back
            // as null INSIDE an HTTP 200
            var html = json.sections && json.sections[CART_SECTION];
            if (html && window.HDCart && window.HDCart.refresh(html)) {
              swapped = true;
              // the add button is the trigger, so closing the drawer returns
              // focus to exactly where the shopper left off
              window.HDCart.open(btn);
              window.HDCart.announce('added');
            }
          } catch (err) {
            /* the add already landed; the drawer is decoration on top of it */
          }
          // the swap carried the authoritative count with it, so /cart.js is
          // only needed when there was no swap
          if (!swapped) return refreshCartCount();
        });
    });
  }

  // Collects the cards at or under root. A descendant selector alone would miss
  // the case callers actually use — root IS the card — because `.product-card x`
  // needs the ancestor to sit inside root, not to be it.
  function cardsIn(node) {
    var found = Array.prototype.slice.call(node.querySelectorAll('.product-card'));
    if (node.matches && node.matches('.product-card')) found.unshift(node);
    return found;
  }

  function init(node) {
    node = node || document;
    cardsIn(node).forEach(function (card) {
      if (card.hasAttribute(MARK)) return;
      card.setAttribute(MARK, '');
      if (card.hasAttribute('data-hover-src')) addHover(card);
      card.querySelectorAll('[data-hd-cart]').forEach(wireCartForm);
    });
  }

  window.HDProductCard = { init: init, wireCartForm: wireCartForm, refreshCartCount: refreshCartCount };

  init();

  // PHASE 3 — the editor-reload gap, closed at the source.
  //
  // This file wired cart forms once at parse time and had no lifecycle hook, so
  // when the theme editor swapped a section's markup in, the fresh <form> was
  // never intercepted and the next add-to-cart click did a NATIVE POST that
  // navigated the preview to /cart. That was measured, not assumed, and
  // sections/hd-featured-products.liquid:75-96 carried a per-section workaround
  // for it because this file was out of that branch's scope
  // (REPORT-PHASE2.md flag 13 records the same gap still open on the collection
  // page and the PDP, which ship this script too).
  //
  // One listener here covers all four surfaces and lets that workaround be
  // deleted. init() is guarded by data-pc-bound, so an overlapping root is safe.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
